/**
 * UGC Video Slider — Infinite scroll
 *
 * Shows 2 videos on mobile, 4 on desktop.
 * Scrolls ONE video at a time with seamless infinite looping.
 * Clones all slides and appends them so the track never "rewinds."
 */
class UgcVideoSlider extends HTMLElement {
  #currentIndex = 0;
  #autoplayTimer = null;
  #autoplayInterval = 3000;
  #originalCount = 0;
  #isTransitioning = false;

  /* Layout values */
  #gap = 16;
  #padding = 16;
  #slidesVisible = 2;
  #slideWidth = 0;

  connectedCallback() {
    this.#autoplayInterval = parseInt(this.dataset.autoplayInterval) || 3000;

    const origSlides = this.querySelectorAll('.ugc-carousel__slide');
    this.#originalCount = origSlides.length;

    if (this.#originalCount === 0) return;

    /* Clone all slides and append for seamless loop */
    const track = this.querySelector('.ugc-carousel__track');
    origSlides.forEach((slide) => {
      const clone = slide.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('data-shopify-editor-block');
      track.appendChild(clone);
    });

    /* Listen for transition end to do seamless jump */
    track.addEventListener('transitionend', () => this.#onTransitionEnd());

    this.#measure();
    this.#applyLayout();
    this.#bindEvents();
    this.#startAutoplay();

    window.addEventListener('resize', this.#onResize);
  }

  disconnectedCallback() {
    this.#stopAutoplay();
    window.removeEventListener('resize', this.#onResize);
  }

  /* ------------------------------------------------------------------ */
  /*  Layout                                                             */
  /* ------------------------------------------------------------------ */

  #onResize = () => {
    this.#measure();
    this.#applyLayout();
    this.#setPosition(false);
  };

  #measure() {
    const vw = this.offsetWidth || window.innerWidth;
    const isDesktop = vw >= 750;

    this.#slidesVisible = isDesktop ? 4 : 2;
    this.#gap = isDesktop ? 20 : 16;
    this.#padding = this.#gap;

    this.#slideWidth =
      (vw - 2 * this.#padding - (this.#slidesVisible - 1) * this.#gap) /
      this.#slidesVisible;
  }

  #applyLayout() {
    const track = this.querySelector('.ugc-carousel__track');
    if (!track) return;

    track.style.paddingLeft = `${this.#padding}px`;
    track.style.paddingRight = `${this.#padding}px`;
    track.style.gap = `${this.#gap}px`;

    const allSlides = this.querySelectorAll('.ugc-carousel__slide');
    allSlides.forEach((slide) => {
      slide.style.width = `${this.#slideWidth}px`;
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Events                                                             */
  /* ------------------------------------------------------------------ */

  #bindEvents() {
    const muteBtns = this.querySelectorAll('.ugc-carousel__mute-btn');
    muteBtns.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.#toggleMute(btn);
      });
    });

    const wrappers = this.querySelectorAll('.ugc-carousel__video-wrapper');
    wrappers.forEach((wrapper) => {
      wrapper.addEventListener('click', () => this.#toggleVideo(wrapper));
    });

    let startX = 0;
    let startTime = 0;
    let isDragging = false;

    this.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startTime = Date.now();
      isDragging = true;
    }, { passive: true });

    this.addEventListener('touchend', (e) => {
      if (!isDragging) return;
      isDragging = false;

      const diff = startX - e.changedTouches[0].clientX;
      const elapsed = Date.now() - startTime;
      const velocity = Math.abs(diff) / elapsed;
      const threshold = velocity > 0.3 ? 20 : 50;

      if (Math.abs(diff) > threshold) {
        if (diff > 0) {
          this.#next();
        } else {
          this.#prev();
        }
        this.#restartAutoplay();
      }
    }, { passive: true });
  }

  /* ------------------------------------------------------------------ */
  /*  Video play/pause                                                   */
  /* ------------------------------------------------------------------ */

  #toggleVideo(wrapper) {
    const video = wrapper.querySelector('video');
    if (!video) return;

    if (video.paused) {
      video.play();
      wrapper.classList.remove('ugc-carousel__video-wrapper--paused');
      this.#startAutoplay();
    } else {
      video.pause();
      wrapper.classList.add('ugc-carousel__video-wrapper--paused');
      this.#stopAutoplay();
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Mute / Unmute                                                      */
  /* ------------------------------------------------------------------ */

  #toggleMute(btn) {
    const wrapper = btn.closest('.ugc-carousel__video-wrapper');
    const video = wrapper?.querySelector('video');
    if (!video) return;

    const muteIcon = btn.querySelector('.ugc-carousel__mute-icon');
    const unmuteIcon = btn.querySelector('.ugc-carousel__unmute-icon');

    if (video.muted) {
      video.muted = false;
      muteIcon.style.display = 'none';
      unmuteIcon.style.display = 'block';
      btn.setAttribute('aria-label', 'Mute video');
    } else {
      video.muted = true;
      muteIcon.style.display = 'block';
      unmuteIcon.style.display = 'none';
      btn.setAttribute('aria-label', 'Unmute video');
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Infinite navigation                                                */
  /* ------------------------------------------------------------------ */

  #setPosition(animate = true) {
    const track = this.querySelector('.ugc-carousel__track');
    if (!track) return;

    const offset = this.#currentIndex * (this.#slideWidth + this.#gap);

    if (!animate) {
      track.style.transition = 'none';
    }

    track.style.transform = `translateX(-${offset}px)`;

    if (!animate) {
      track.offsetHeight; /* force reflow */
      track.style.transition = '';
    }
  }

  #onTransitionEnd() {
    /* If we've scrolled into the cloned region, jump back seamlessly */
    if (this.#currentIndex >= this.#originalCount) {
      this.#currentIndex = this.#currentIndex - this.#originalCount;
      this.#setPosition(false);
    } else if (this.#currentIndex < 0) {
      this.#currentIndex = this.#currentIndex + this.#originalCount;
      this.#setPosition(false);
    }
    this.#isTransitioning = false;
  }

  #next() {
    if (this.#isTransitioning) return;
    this.#isTransitioning = true;
    this.#currentIndex += 1;
    this.#setPosition(true);
  }

  #prev() {
    if (this.#isTransitioning) return;

    /* If at index 0, jump to cloned end first (no animation), then animate back */
    if (this.#currentIndex === 0) {
      this.#currentIndex = this.#originalCount;
      this.#setPosition(false);
      /* Small delay to let browser paint, then animate */
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.#isTransitioning = true;
          this.#currentIndex -= 1;
          this.#setPosition(true);
        });
      });
      return;
    }

    this.#isTransitioning = true;
    this.#currentIndex -= 1;
    this.#setPosition(true);
  }

  /* ------------------------------------------------------------------ */
  /*  Autoplay                                                           */
  /* ------------------------------------------------------------------ */

  #startAutoplay() {
    this.#stopAutoplay();
    this.#autoplayTimer = setInterval(() => this.#next(), this.#autoplayInterval);
  }

  #stopAutoplay() {
    if (this.#autoplayTimer) {
      clearInterval(this.#autoplayTimer);
      this.#autoplayTimer = null;
    }
  }

  #restartAutoplay() {
    this.#startAutoplay();
  }
}

if (!customElements.get('ugc-video-slider')) {
  customElements.define('ugc-video-slider', UgcVideoSlider);
}
